import type { EventDescription } from '~/composables/types/event'
import type { AlgoliaEventItem } from '~/composables/types/algolia'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'
import { sanitizeAlgoliaQueryId } from '~/utils/helpers'

interface PurchaseEventItem {
  eventDescriptionId: string
  queryID?: string
  price?: number
  quantity?: number
}

export const useAlgoliaConversion = () => {
  const { locale } = useI18n()
  const route = useRoute()
  const { client: algoliaInsightsClient, token } = useAlgoliaSearchInsights()

  const sendPurchaseEvent = (items: PurchaseEventItem[]) => {
    /* @see https://www.algolia.com/doc/libraries/search-insights/purchased-object-ids-after-search */
    // Filter out null/undefined items first for defensive programming
    const validItems = items?.filter((item) => item != null) || []

    if (!validItems || validItems.length === 0) {
      // eslint-disable-next-line no-console
      console.error('sendPurchaseEvent: no valid items provided')
      return
    }

    // Build objectIDs array
    const objectIDs = validItems.map(
      (item) => 'EventDescription::' + item.eventDescriptionId
    )

    // Build objectData array
    const objectData = validItems.map((item) => {
      const data: any = {}
      // Sanitize queryID before including it
      const sanitizedQueryID = sanitizeAlgoliaQueryId(item.queryID)
      if (sanitizedQueryID) {
        data.queryID = sanitizedQueryID
      }
      if (item.price !== undefined) {
        data.price = item.price
      }
      if (item.quantity !== undefined) {
        data.quantity = item.quantity
      }
      return data
    })

    try {
      algoliaInsightsClient('purchasedObjectIDsAfterSearch', {
        userToken: token.value,
        index: getEventListIndexName(locale.value),
        eventName: 'Product Purchased',
        objectIDs,
        objectData,
        currency: 'EUR',
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  const sendAddToCartEvent = (event: EventDescription) => {
    const rawQueryId = route?.query?.qid?.toString() || null
    const queryId = sanitizeAlgoliaQueryId(rawQueryId)
    const algoliaInsightsMethod = queryId
      ? 'addedToCartObjectIDsAfterSearch'
      : 'addedToCartObjectIDs'

    try {
      algoliaInsightsClient(algoliaInsightsMethod, {
        userToken: token.value,
        index: getEventListIndexName(locale.value),
        eventName: 'Event added to cart - Event page',
        ...(queryId && {
          queryId,
        }),
        objectIDs: ['EventDescription::' + eventGetters.getId(event)],
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }
  const sendViewEvents = (event: EventDescription) => {
    const rawQueryId = route.query.qid?.toString() || null
    const queryId = sanitizeAlgoliaQueryId(rawQueryId)

    if (token.value) {
      try {
        /* @see https://www.algolia.com/doc/api-reference/api-methods/viewed-object-ids/ */
        algoliaInsightsClient('viewedObjectIDs', {
          userToken: token.value,
          ...(queryId && {
            queryId,
          }),
          index: getEventListIndexName(locale.value),
          eventName: 'Event Viewed - Event page',
          objectIDs: ['EventDescription::' + eventGetters.getId(event)],
        })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
      }
    }
  }

  const sendViewedObjectIDs = (
    items: AlgoliaEventItem[],
    eventName: string,
    indexName?: string
  ) => {
    if (!token.value || !items || items.length === 0) {
      return
    }

    // Limit to 20 items as per Algolia documentation
    const limitedItems = items.slice(0, 20)
    const objectIDs = limitedItems.map(
      (item) => 'EventDescription::' + algoliaEventGetters.fnGetHashedId(item)
    )

    try {
      algoliaInsightsClient('viewedObjectIDs', {
        userToken: token.value,
        index: indexName || getEventListIndexName(locale.value),
        eventName,
        objectIDs,
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  return {
    sendPurchaseEvent,
    sendAddToCartEvent,
    sendViewEvents,
    sendViewedObjectIDs,
  }
}
